import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { PurgeMasterDialog } from '@/components/efs/PurgeMasterDialog';

import { PackageTypesFormDialog } from './PackageTypesFormDialog';
import {
  useActivatePackageType,
  useDeletePackageType,
  usePurgePackageType,
} from './usePackageTypes';
import type { PackageType } from './types';

interface PackageTypesRowActionsProps {
  row: PackageType;
}

/**
 * CRUD menu for package types. Manager+ roles only — viewers see nothing.
 * Delete is a soft-delete on the backend (sets `active_flag = false`).
 */
export const PackageTypesRowActions = ({ row }: PackageTypesRowActionsProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const del = useDeletePackageType();
  const activate = useActivatePackageType();
  const purge = usePurgePackageType();
  const [editOpen, setEditOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const canManage = hasPermission(user, 'master.approve');
  if (!canManage) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('efs.masters.actions.row_menu', { ns: 'masters' })}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setEditOpen(true)}>
            {t('efs.masters.actions.edit', { ns: 'masters' })}
          </DropdownMenuItem>
          {row.active_flag ? (
            <DropdownMenuItem
              onSelect={() => del.mutate(row.package_type_id)}
              disabled={del.isPending}
            >
              {t('efs.masters.package_types.actions.deactivate', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem
                onSelect={() => activate.mutate(row.package_type_id)}
                disabled={activate.isPending}
              >
                {t('efs.masters.package_types.actions.activate', { ns: 'masters' })}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  purge.reset();
                  setPurgeOpen(true);
                }}
                className="text-red-900 focus:text-red-900"
              >
                {t('efs.masters.actions.purge', { ns: 'masters' })}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <PurgeMasterDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        name={row.display_name}
        isPending={purge.isPending}
        error={purge.error}
        onConfirm={() =>
          purge.mutate(row.package_type_id, { onSuccess: () => setPurgeOpen(false) })
        }
      />

      <PackageTypesFormDialog open={editOpen} onOpenChange={setEditOpen} row={row} />
    </>
  );
};
